import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { PartnerPicker, type PickerPartner } from '../PartnerPicker';
import { noFilters, type CustomerFilterSet as Filters } from './customer-types';
import { packageWord } from './customer-words';
import './customers.css';

export interface CustomerFiltersProps {
  filters: Filters;
  /** A change to one or more filters; the page writes it to the URL and goes back to the first page. */
  onChange: (change: Partial<Filters>) => void;
  /** The packages present under the filters, most companies first; absent while the numbers load. */
  plans: readonly { plan: string; companies: number }[] | undefined;
  /** The partner picker's question to the server. */
  searchPartners: (q: string) => Promise<PickerPartner[]>;
  /** How long typing rests before the server is asked. */
  debounceMs?: number;
}

type Switch = 'paying' | 'pastDue' | 'test' | 'problems';

const SWITCHES: readonly { key: Switch; label: string; title: string }[] = [
  { key: 'paying', label: 'Paying', title: 'Card money received in the period.' },
  { key: 'pastDue', label: 'Past due', title: 'A card that stopped paying.' },
  { key: 'test', label: 'Test only', title: "Only Ademero's own test companies." },
  {
    key: 'problems',
    label: 'Has problems',
    title: 'Work that stopped, an export that failed in 24 hours, or work held for pages.',
  },
];

/** True when anything narrows the list besides the saved view. */
export function anyFilter(f: Filters): boolean {
  return f.q !== '' || f.plan !== null || f.partner !== null || SWITCHES.some(({ key }) => f[key]);
}

/**
 * The search box: what is typed shows at once and is sent once typing rests, so the server is asked
 * for "acme", not for "a", "ac" and "acm". A change from outside (Clear filters, a saved view) replaces it.
 */
function SearchBox(props: { q: string; onSearch: (q: string) => void; debounceMs: number }) {
  const { q, onSearch, debounceMs } = props;
  const [text, setText] = useState(q);
  const sent = useRef(q);
  useEffect(() => {
    if (q !== sent.current) {
      sent.current = q;
      setText(q);
    }
  }, [q]);
  useEffect(() => {
    if (text.trim() === sent.current) return undefined;
    const timer = setTimeout(() => {
      sent.current = text.trim();
      onSearch(text.trim());
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [text, debounceMs, onSearch]);
  return (
    <input
      type="search"
      className={`ad-cu-search${q ? ' ad-cu-on' : ''}`}
      aria-label="Find a customer"
      placeholder="Find a customer"
      title="By name, web address, email domain or the start of an id"
      maxLength={80}
      value={text}
      onChange={(e) => setText(e.currentTarget.value)}
    />
  );
}

/** The package filter: one option a package present, with its count. Bounded by the packages there are. */
function PackageMenu({ filters, plans, onChange }: CustomerFiltersProps): ReactNode {
  const known = plans ?? [];
  const listed =
    filters.plan && !known.some((p) => p.plan === filters.plan)
      ? [...known, { plan: filters.plan, companies: 0 }]
      : known;
  return (
    <select
      className={`ad-cu-select${filters.plan ? ' ad-cu-on' : ''}`}
      aria-label="Package"
      value={filters.plan ?? ''}
      onChange={(e) => onChange({ plan: e.currentTarget.value || null })}
    >
      <option value="">Every package</option>
      {listed.map((p) => (
        <option key={p.plan} value={p.plan}>
          {packageWord(p.plan)} ({p.companies.toLocaleString('en-US')})
        </option>
      ))}
    </select>
  );
}

/** A switch as a chip: pressed is drawn on, with a tick, so it never speaks by colour alone. */
function SwitchChip(props: { label: string; title: string; on: boolean; onPress: () => void }) {
  return (
    <button
      type="button"
      className="ad-cu-chip"
      aria-pressed={props.on}
      title={props.title}
      onClick={props.onPress}
    >
      {props.on ? (
        <span className="ad-cu-chip__on" aria-hidden="true">
          ✓
        </span>
      ) : null}
      {props.label}
    </button>
  );
}

/**
 * One row: the search, the package, four switches and the partner. Everything asked here is answered by
 * the ledger; what is on is drawn on, and one press clears the lot.
 */
export function CustomerFilters(props: CustomerFiltersProps): ReactNode {
  const { filters, onChange } = props;
  const onSearch = useLatest((q: string) => onChange({ q }));
  return (
    <div className="ad-cu-filters" role="search" aria-label="Filter the customers">
      <SearchBox q={filters.q} onSearch={onSearch} debounceMs={props.debounceMs ?? 250} />
      <PackageMenu {...props} />
      {SWITCHES.map((s) => (
        <SwitchChip
          key={s.key}
          label={s.label}
          title={s.title}
          on={filters[s.key]}
          onPress={() => onChange({ [s.key]: !filters[s.key] })}
        />
      ))}
      <PartnerPicker
        value={filters.partner}
        onChange={(partner) => onChange({ partner })}
        search={props.searchPartners}
      />
      {anyFilter(filters) ? (
        <button
          type="button"
          className="ad-cu-link"
          onClick={() => onChange({ ...noFilters, view: filters.view })}
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );
}

/** A function that stays the same while always calling the newest one given. */
function useLatest<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void {
  const latest = useRef(fn);
  useEffect(() => {
    latest.current = fn;
  });
  return useCallback((...args: A) => latest.current(...args), []);
}
